import { type Driver, GraphQLCameraTripImageRequester } from '@cobliteam/api-lib';
import {
  type DriverNominateTripImageInput,
  type DriverVisibility,
  IdentificationMethod,
  type PageableContent,
  type SendObstructionFeedbackInput,
  type TimestampInMillis,
  type TripImageDetails,
  type TripImageHistory,
  type UUID,
  type VehicleTimestampImages,
} from '@cobliteam/common-types';
import Alert from '@cobliteam/ui/components/Alert';
import { type InfiniteData, type QueryKey, type UseQueryOptions, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { QUERY_KEY as QUERY_KEY_USE_DRIVERS } from '../../useEntity/useDriver';

import useUser from '../../useEntity/useUser';

type Sort = { key: string; desc: 'asc' | 'desc' } | undefined;

interface PageWrapper<T> {
  pageParams: number[];
  pages: PageableContent<T>[];
}

const QUERY_KEY = ['CAMERA-TRIP-IMAGE'];
const QUERY_KEY_LIST = [...QUERY_KEY, 'LIST'];
const QUERY_KEY_VEHICLE_TIMESTAMP_IMAGES = ['VEHICLE-TIMESTAMP-IMAGES'];
const DRIVERS_QUERY_KEY = [QUERY_KEY_USE_DRIVERS];

const useCameraTripImage = () => {
  const cameraTripImageRequester = new GraphQLCameraTripImageRequester();

  const queryClient = useQueryClient();

  const { getUser } = useUser();
  const user = getUser();

  const useTripImage = (
    fromTimestamp?: TimestampInMillis,
    toTimestamp?: TimestampInMillis,
    identificationMethod?: IdentificationMethod[],
    driverVisibilities?: DriverVisibility[],
    vehicleIds?: UUID[],
    driverIds?: UUID[],
    isObstructed?: boolean | null,
    page?: number,
    size?: number,
    sort?: Sort,
    options?: UseQueryOptions<PageableContent<TripImageHistory>, 'queryKey'>
  ) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<PageableContent<TripImageHistory>, 'queryKey'>({
      queryKey: [
        ...QUERY_KEY_LIST,
        {
          fromTimestamp,
          toTimestamp,
          identificationMethod: JSON.stringify(identificationMethod),
          vehicleIds,
          driverIds,
          page,
          size,
          sort,
        },
      ],
      queryFn: () =>
        cameraTripImageRequester.getTripImagePage(
          fromTimestamp,
          toTimestamp,
          identificationMethod?.length === 0 ? undefined : identificationMethod,
          driverVisibilities?.length === 0 ? undefined : driverVisibilities,
          vehicleIds,
          driverIds,
          isObstructed,
          page,
          size,
          sort
        ),
      retry: false,
      enabled: true,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 300000,
      ...options,
    });
  };

  const useGetTripImage = (
    fromTimestamp?: TimestampInMillis,
    toTimestamp?: TimestampInMillis,
    identificationMethod?: IdentificationMethod[],
    driverVisibilities?: DriverVisibility[],
    vehicleIds?: UUID[],
    driverIds?: UUID[],
    isObstructed?: boolean | null,
    page?: number,
    size?: number,
    sort?: Sort
  ) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useInfiniteQuery<PageableContent<TripImageHistory>>({
      initialPageParam: 0,
      queryKey: [
        ...QUERY_KEY_LIST,
        {
          fromTimestamp,
          toTimestamp,
          identificationMethod: JSON.stringify(identificationMethod),
          driverVisibilities: JSON.stringify(driverVisibilities),
          vehicleIds,
          driverIds,
          isObstructed,
          page,
          size,
          sort,
        },
      ],
      queryFn: ({ pageParam = 0 }) =>
        cameraTripImageRequester.getTripImagePage(
          fromTimestamp,
          toTimestamp,
          identificationMethod?.length === 0 ? undefined : identificationMethod,
          driverVisibilities?.length === 0 ? undefined : driverVisibilities,
          vehicleIds && vehicleIds.length > 0 ? vehicleIds : undefined,
          driverIds && driverIds.length > 0 ? driverIds : undefined,
          isObstructed,
          pageParam,
          size,
          sort
        ),
      staleTime: 300000,
      getNextPageParam: (lastPage, pages) => {
        if (lastPage.last === false) return pages.length;
        return undefined;
      },
    });
  };

  const useTripImagesDetails = (tripId: string) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<TripImageHistory>({
      queryKey: [...QUERY_KEY, 'VIEW', tripId],
      queryFn: () => cameraTripImageRequester.getTripImagesDetails(tripId),
      retry: false,
      enabled: !!tripId,
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      staleTime: 300000,
    });
  };

  const useVehicleTimestampImagesDetails = (enabled: boolean, vehicleId?: string, imageTimestamp?: number, imagesBefore = 1) => {
    'use no memo'; // nested in a function the compiler skips; compiled alone it loses the outer scope
    return useQuery<VehicleTimestampImages>({
      queryKey: [...QUERY_KEY_VEHICLE_TIMESTAMP_IMAGES, vehicleId, imageTimestamp, imagesBefore],
      queryFn: () => cameraTripImageRequester.getVehicleTimestampImagesDetails(vehicleId, imageTimestamp, imagesBefore),
      enabled,
    });
  };

  const driverNominateTripImage = useMutation<boolean, unknown, { driverNominateTripImageInput: DriverNominateTripImageInput[] }>({
    mutationFn: (request) => cameraTripImageRequester.driverNominateTripImage(request.driverNominateTripImageInput),
    onMutate: async (request) => {
      const driversQueryData: [QueryKey, Driver[] | undefined][] = queryClient.getQueriesData({
        queryKey: DRIVERS_QUERY_KEY,
      });

      const [_, driversData]: [QueryKey, Driver[] | undefined] = driversQueryData[0];

      const foundDriver = driversData?.find((driver) => driver.id === request.driverNominateTripImageInput[0].driverId);
      queryClient.setQueriesData(
        {
          queryKey: QUERY_KEY_LIST,
        },
        (cachedContent: PageWrapper<TripImageHistory> | undefined) => {
          if (!cachedContent) return cachedContent;

          const updatedPages = cachedContent.pages.map((page: PageableContent<TripImageHistory>) => {
            return {
              ...page,
              content: page.content.map((trip: TripImageHistory) => {
                if (trip.tripId === request.driverNominateTripImageInput[0].tripId) {
                  return {
                    ...trip,
                    nominatedAt: moment.now().valueOf(),
                    nominatedBy: { id: user.id, name: user.name },
                    userNominatedDriver: foundDriver,
                    identificationMethod: request.driverNominateTripImageInput[0].operation === 'CONFIRM' ? IdentificationMethod.USER_CONFIRMATION : IdentificationMethod.USER_EDIT,
                  };
                }
                return trip;
              }),
            };
          });

          return {
            ...cachedContent,
            pages: updatedPages,
          };
        }
      );

      queryClient.setQueriesData(
        {
          queryKey: [...QUERY_KEY, 'VIEW', request.driverNominateTripImageInput[0].tripId],
        },
        (old: TripImageHistory | undefined) => {
          if (!old) return old;
          return {
            ...old,
            nominatedAt: moment.now().valueOf(),
            nominatedBy: { id: user.id, name: user.name },
            userNominatedDriver: foundDriver,
            identificationMethod: request.driverNominateTripImageInput[0].operation === 'CONFIRM' ? IdentificationMethod.USER_CONFIRMATION : IdentificationMethod.USER_EDIT,
          };
        }
      );
    },
    onSuccess: () => {
      Alert.success('Obrigado por revisar esta identificação!');
    },
    onError: (error, variables, context: any) => {
      queryClient.setQueriesData<InfiniteData<PageableContent<TripImageHistory>>>(
        {
          queryKey: QUERY_KEY_LIST,
        },
        (old: InfiniteData<PageableContent<TripImageHistory>> | undefined) => {
          if (!old) return old;

          return {
            ...old,
            pages: context?.previousData[0][1].pages,
          };
        }
      );
      queryClient.setQueryData([...QUERY_KEY, 'VIEW', variables.driverNominateTripImageInput[0].tripId], context.previousModalData);
      Alert.error('Ops! Houve um problema ao registrar sua revisão. Por favor, tente novamente mais tarde');
    },
  });

  const sendObstructionFeedback = useMutation<boolean, unknown, SendObstructionFeedbackInput>({
    mutationFn: (request) => cameraTripImageRequester.sendObstructionFeedback(request),
    onMutate: async (request) => {
      const tripDetailsQueryKey = [...QUERY_KEY, 'VIEW', request.tripId];

      const previousModalData = queryClient.getQueriesData({
        queryKey: tripDetailsQueryKey,
      });

      queryClient.setQueriesData(
        {
          queryKey: tripDetailsQueryKey,
        },
        (currentTripImageHistory?: TripImageHistory) => {
          if (!currentTripImageHistory) return currentTripImageHistory;

          return {
            ...currentTripImageHistory,
            tripImages: currentTripImageHistory.tripImages?.map((tripImage: TripImageDetails) => {
              if (tripImage.id !== request.imageId) return tripImage;

              return {
                ...tripImage,
                obstructionFeedback: request.obstructionFeedback,
              };
            }),
          };
        }
      );

      return { previousModalData };
    },
    onSuccess: () => {
      Alert.success('Feedback recebido com sucesso!');
    },
    onError: (_, request, context: any) => {
      const tripDetailsQueryKey = [...QUERY_KEY, 'VIEW', request.tripId];
      queryClient.setQueryData(tripDetailsQueryKey, context.previousModalData);
      Alert.error('Ops! Houve um problema ao enviar seu feedback. Por favor, tente novamente mais tarde');
    },
  });

  return {
    useTripImage,
    useTripImagesDetails,
    useVehicleTimestampImagesDetails,
    driverNominateTripImage,
    sendObstructionFeedback,
    useGetTripImage,
  };
};

export default useCameraTripImage;
